class WishlistSummaryDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-summary-panel]');
    this.overlay = this.querySelector('[data-summary-overlay]');
    this.items = this.querySelector('[data-summary-items]');
    this.empty = this.querySelector('[data-summary-empty]');
    this.removeIconHtml = this.querySelector('[data-summary-remove-icon]')?.innerHTML || '×';
    this.loadingTemplate = this.querySelector('[data-summary-loading-template]');
    this.itemTemplate = this.querySelector('[data-summary-item-template]');
    this.closeButton = this.querySelector('[data-summary-close]');
    this.closeButton.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    this.initSwipeToClose(this.panel, this.querySelector('[data-summary-swipe-handle]'));
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-wishlist-summary-trigger]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      this.open(trigger);
    }, true);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') this.close();
    });
    window.addEventListener('wishlist:updated', (event) => {
      // Skip when we were the dispatcher — removeFromWishlist()/removeFromWishlistSilently()
      // already update this drawer's own DOM directly, so a full reload here is redundant.
      if (event.detail?.source === this) return;
      if (this.getAttribute('aria-hidden') === 'false') this.load();
    });
  }

  open(trigger) {
    this.cancelHideTimer();
    this.trigger = trigger;
    this.classList.remove('invisible');
    this.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      this.overlay.classList.remove('opacity-0');
      this.panel.classList.remove('translate-y-full', 'lg:translate-x-full');
    });
    // enableScrollLock/disableScrollLock live in cart.js, which some pages (404, password,
    // ...) don't load.
    if (typeof window.enableScrollLock === 'function') {
      window.enableScrollLock(this);
    }
    this.closeButton.focus();
    this.load();
  }

  load() {
    if (!window._swat) {
      this.items.replaceChildren(this.loadingTemplate.content.cloneNode(true));
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(() => this.load());
      return;
    }
    window._swat.fetch((products) => this.render(products || []));
  }

  render(products) {
    this.empty.classList.toggle('hidden', products.length > 0);
    this.items.replaceChildren(...products.map((item) => this.buildItem(item)));
  }

  buildItem(item) {
    const article = this.itemTemplate.content.firstElementChild.cloneNode(true);
    article.dataset.summaryItem = item.epi;
    const variantUrl = `${item.du}${item.du?.includes('?') ? '&' : '?'}variant=${item.epi}`;

    const link = article.querySelector('[data-summary-item-link]');
    link.href = variantUrl;
    const image = article.querySelector('[data-summary-item-image]');
    image.src = item.iu || '';
    image.alt = item.dt || '';

    const titleLink = article.querySelector('[data-summary-item-title-link]');
    titleLink.href = variantUrl;
    titleLink.textContent = item.dt || '';

    const sizeEl = article.querySelector('[data-summary-item-size]');
    if (item.vi) {
      sizeEl.textContent = `${this.dataset.sizeLabel || 'Size'}: ${item.vi.split('/')[0]}`;
    } else {
      sizeEl.remove();
    }

    article.querySelector('[data-summary-item-price]').textContent = this.formatPrice(item.pr);

    const addButton = article.querySelector('[data-summary-add]');
    addButton.dataset.variantId = item.epi;
    addButton.addEventListener('click', () => this.addToCart(addButton, item));

    const removeButton = article.querySelector('[data-summary-remove]');
    const removeLabel = `${this.dataset.removeLabel || 'Remove'} ${item.dt || ''}`;
    removeButton.setAttribute('aria-label', removeLabel);
    removeButton.title = removeLabel;
    removeButton.dataset.productId = item.empi;
    removeButton.dataset.variantId = item.epi;
    removeButton.dataset.productUrl = item.du || '';
    removeButton.innerHTML = this.removeIconHtml;
    removeButton.addEventListener('click', () => this.removeFromWishlist(removeButton));

    return article;
  }

  formatPrice(dollars) {
    return window.Shopify.formatMoney(Number(dollars || 0) * 100, window.Shopify.money_format);
  }

  removeFromWishlist(button) {
    button.disabled = true;
    const item = button.closest('[data-summary-item]');
    const payload = {
      empi: Number(button.dataset.productId),
      epi: Number(button.dataset.variantId),
      du: button.dataset.productUrl,
    };
    // Optimistic: empty the icon/collapse the row immediately for INP rather than waiting on
    // the network round trip. Reconcile from the server (re-filling the item) on failure.
    window._swat.api.removeFromWishList(payload, () => {
      window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: this } }));
    }, () => {
      console.error('Failed to remove item from wishlist', payload);
      this.load();
    });
    item.classList.remove('py-4');
    window.collapse(item).then(() => {
      item.remove();
      const remaining = this.items.children.length;
      this.empty.classList.toggle('hidden', remaining > 0);
      this.updateHeaderWishlistCount(remaining);
    });
  }

  updateHeaderWishlistCount(count) {
    document.querySelectorAll('.wishlist-count').forEach((element) => {
      element.dataset.count = String(count);
      const ariaLiveElement = element.querySelector('[aria-live]');
      if (ariaLiveElement) ariaLiveElement.textContent = String(count);
    });
  }

  async addToCart(button, item) {
    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = this.dataset.addingLabel || 'Adding…';
    const error = button.parentElement.querySelector('[data-summary-error]');
    error.textContent = '';
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(button.dataset.variantId), quantity: 1, ...(window.cartAddSections || {}) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || window.cartStrings?.addToCartError || 'Unable to add item.');
      const cartContentHtml = window.extractCartContentHtml ? window.extractCartContentHtml(data) : null;
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: { cartContentHtml } }));
      window.dispatchEvent(new CustomEvent('cart-drawer:open'));
      this.removeFromWishlistSilently(item);
      this.close();
    } catch (exception) {
      error.textContent = exception.message;
    } finally {
      button.disabled = false;
      button.textContent = originalLabel;
    }
  }

  // Cart add succeeded, so the item shouldn't linger in the wishlist — the
  // drawer is closing regardless, so skip removeFromWishlist's row
  // collapse/removal and just sync Swym + the rest of the page's wishlist state.
  removeFromWishlistSilently(item) {
    const payload = { empi: Number(item.empi), epi: Number(item.epi), du: item.du };
    window._swat.api.removeFromWishList(payload, () => {
      window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: this } }));
      window._swat.fetch((products) => this.updateHeaderWishlistCount((products || []).length));
    });
  }

  close() {
    this.overlay.classList.add('opacity-0');
    this.panel.classList.add('translate-y-full', 'lg:translate-x-full');
    this.setAttribute('aria-hidden', 'true');
    if (typeof window.disableScrollLock === 'function') {
      window.disableScrollLock(this);
    }
    this.hideAfterPanelTransition();
    this.trigger?.focus();
  }

  // Waits for the panel's actual slide-out transition to finish (the CSS uses 700ms) rather than
  // a fixed guess, so the panel never gets hidden mid-animation. Falls back to a timeout in case
  // transitionend never fires (e.g. reduced motion).
  hideAfterPanelTransition() {
    this.cancelHideTimer();
    this.hideTransitionListener = (event) => {
      if (event.target === this.panel && event.propertyName === 'transform') this.finishHide();
    };
    this.panel.addEventListener('transitionend', this.hideTransitionListener);
    this.hideTimeoutId = setTimeout(() => this.finishHide(), 750);
  }

  finishHide() {
    this.cancelHideTimer();
    this.classList.add('invisible');
  }

  cancelHideTimer() {
    if (this.hideTransitionListener) {
      this.panel.removeEventListener('transitionend', this.hideTransitionListener);
      this.hideTransitionListener = null;
    }
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  initSwipeToClose(panel, handle) {
    if (!panel || !handle) return;
    const threshold = 50;
    let startY = null;
    let lastY = null;
    let movingUp = false;
    const reset = () => {
      startY = null;
      lastY = null;
      movingUp = false;
      panel.style.transition = '';
      panel.style.transform = '';
    };
    panel.addEventListener('touchstart', (event) => {
      if (window.innerWidth >= 1024) return;
      const touchY = event.touches[0].clientY;
      startY = touchY <= handle.getBoundingClientRect().bottom ? touchY : null;
      lastY = startY;
      movingUp = false;
    }, { passive: true });
    panel.addEventListener('touchmove', (event) => {
      if (startY === null) return;
      // Follow the thumb 1:1 (no transition) while dragging; touchend either completes the
      // close from wherever the finger let go, or clears this so the panel snaps back open.
      const touchY = event.touches[0].clientY;
      movingUp = touchY < lastY;
      lastY = touchY;
      const deltaY = Math.max(0, touchY - startY);
      event.preventDefault();
      panel.style.transition = 'none';
      panel.style.transform = `translateY(${deltaY}px)`;
    }, { passive: false });
    panel.addEventListener('touchend', (event) => {
      if (startY === null) return;
      const deltaY = Math.max(0, event.changedTouches[0].clientY - startY);
      // Releasing while still moving upward always snaps back open, even past the threshold —
      // only a release while moving down (or at rest) past the threshold closes the panel.
      const shouldClose = !movingUp && deltaY > threshold;
      reset();
      if (shouldClose) this.close();
    });
    panel.addEventListener('touchcancel', reset);
  }
}
if (!customElements.get('wishlist-summary-drawer')) customElements.define('wishlist-summary-drawer', WishlistSummaryDrawer);
